// src/redux/thunks/trashThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosExtraction } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";

/* =========================
   ✅ Helpers de normalisation
========================= */
const bytesToHuman = (bytes) => {
  const b = Number(bytes);
  if (!b || Number.isNaN(b)) return "-";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = b;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDateSafe = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleDateString();
};

const normalizeKey = (v) =>
  String(v || "")
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_")
    .trim();

/**
 * ✅ Canonical TYPE (ce que ton Select utilise)
 * Select values: bilan | irm | echographie | rapport | ordonnance | compte_rendu | autre | all
 */
const toCanonicalType = (raw) => {
  const v = normalizeKey(raw);

  const map = {
    // bilan
    bilan_biochimique: "bilan",
    biochemistry_tests: "bilan",
    biochemistry: "bilan",
    bilan: "bilan",

    // irm
    irm: "irm",
    mri: "irm",
    mri_scans: "irm",

    // echographie
    echographie: "echographie",
    ultrasound: "echographie",
    medical_ultrasound: "echographie",

    // rapport / radiography
    radiographie: "rapport",
    radiography: "rapport",
    x_ray: "rapport",
    xray: "rapport",
    rapport: "rapport",

    // ordonnance
    ordonnance: "ordonnance",
    prescription: "ordonnance",
    medical_prescription: "ordonnance",

    // compte rendu
    compte_rendu: "compte_rendu",
    medical_reports: "compte_rendu",
    reports: "compte_rendu",

    // autre
    autre: "autre",
    other: "autre",
    others: "autre",
  };

  if (v.includes("autre") || v.includes("other")) return "autre";

  return map[v] || v || "all";
};

const normalizeTrashItem = (it) => {
  const trashId = it.trashId || it._id || it.id;

  const fileName =
    it.fileName ||
    it.nom_fichier ||
    it.filename ||
    it.originalFileName ||
    it.name ||
    "Unnamed";

  // ✅ location affichée (dans ton cas c'est souvent "-")
  const rawLocation =
    it.originalCollection ||
    it.sourceCollection ||
    it.original_location ||
    it.location ||
    it.collection ||
    it.originalLocation ||
    it.folder ||
    "-";

  const location = rawLocation;

  // ✅ IMPORTANT: TYPE réel (celui affiché dans "Item type")
  const rawType =
    it.type ||
    it.type_document ||
    it.documentType ||
    it.docType ||
    it.mimeType ||
    it.itemType ||
    "-";

  const type = String(rawType || "-");

  // ✅ canonicalType = ce qui va servir au filtre Select
  const canonicalType = toCanonicalType(rawType);

  const deletedRaw =
    it.deletedDate ||
    it.deletedAt ||
    it.deleted_at ||
    it.movedToTrashAt ||
    it.trashedAt ||
    it.createdAt;

  const deletedDate = formatDateSafe(deletedRaw);

  const sizeRaw =
    it.size ||
    it.sizeBytes ||
    it.size_bytes ||
    it.file_size ||
    it.fileSize ||
    it.bytes;

  const size = typeof sizeRaw === "string" ? sizeRaw : bytesToHuman(sizeRaw);

  return {
    ...it,
    trashId,
    key: trashId, // ✅ AntD rowKey
    fileName,
    location,
    deletedDate,
    size,
    type,          // affichage
    canonicalType, // ✅ filtrage
  };
};

/**
 * ✅ Déplacer un document vers la corbeille
 */
export const moveDocumentToTrash = createAsyncThunk(
  "trash/moveDocumentToTrash",
  async ({ collection, documentId, userId = null, version = null }, { rejectWithValue }) => {
    try {
      const response = await axiosExtraction.post(
        ENDPOINTS.EXTRACTION.TRASH.MOVE(collection, documentId),
        {},
        {
          params: userId ? { user_id: userId } : undefined,
          headers:
            version != null ? { "X-Resource-Version": String(version) } : undefined,
        },
      );
      return { collection, documentId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors du déplacement vers la corbeille"
      );
    }
  }
);

/**
 * ✅ Récupérer tous les documents supprimés
 */
export const fetchDeletedDocuments = createAsyncThunk(
  "trash/fetchDeletedDocuments",
  async ({ skip = 0, limit = 50 } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosExtraction.get(
        ENDPOINTS.EXTRACTION.TRASH.LIST,
        { params: { skip, limit } }
      );

      const rawItems = response.data.documents || response.data.items || [];
      const items = rawItems.map(normalizeTrashItem).filter((x) => x.trashId);

      // ✅ Debug utile

      return {
        items,
        total: response.data.total ?? response.data.count ?? items.length,
        totalSize: response.data.totalSize ?? 0,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Erreur lors du chargement"
      );
    }
  }
);

/**
 * ✅ Restaurer un document
 */
export const restoreDocument = createAsyncThunk(
  "trash/restoreDocument",
  async ({ trashId }, { rejectWithValue }) => {
    try {
      const endpoint = ENDPOINTS.EXTRACTION.TRASH.RESTORE(trashId);
      const response = await axiosExtraction.post(endpoint);
      return { trashId, key: trashId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Erreur lors de la restauration"
      );
    }
  }
);

/**
 * ✅ Supprimer définitivement un document
 */
export const permanentlyDeleteDocument = createAsyncThunk(
  "trash/permanentlyDeleteDocument",
  async ({ trashId }, { rejectWithValue }) => {
    try {
      const endpoint = ENDPOINTS.EXTRACTION.TRASH.PERMANENT_DELETE(trashId);
      const response = await axiosExtraction.delete(endpoint);
      return { trashId, key: trashId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Erreur lors de la suppression"
      );
    }
  }
);

/**
 * ✅ Restaurer plusieurs documents
 */
export const bulkRestoreDocuments = createAsyncThunk(
  "trash/bulkRestoreDocuments",
  async ({ documentIds }, { rejectWithValue }) => {
    try {
      const response = await axiosExtraction.post(
        ENDPOINTS.EXTRACTION.TRASH.BULK_RESTORE,
        { documentIds }
      );
      return { restoredKeys: documentIds, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Erreur lors de la restauration multiple"
      );
    }
  }
);

/**
 * ✅ Supprimer plusieurs documents définitivement
 */
export const bulkDeleteDocuments = createAsyncThunk(
  "trash/bulkDeleteDocuments",
  async ({ documentIds }, { rejectWithValue }) => {
    try {
      const response = await axiosExtraction.post(
        ENDPOINTS.EXTRACTION.TRASH.BULK_DELETE,
        { documentIds }
      );
      return { deletedKeys: documentIds, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Erreur lors de la suppression multiple"
      );
    }
  }
);

/**
 * ✅ Vider la corbeille
 */
export const emptyTrash = createAsyncThunk(
  "trash/emptyTrash",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosExtraction.delete(
        ENDPOINTS.EXTRACTION.TRASH.EMPTY
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Erreur lors du vidage de la corbeille"
      );
    }
  }
);
